import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Slider, Box } from "@mui/material";
import { Link } from "react-router-dom";
import { CiFilter } from "react-icons/ci";
import { RiFilter2Fill } from "react-icons/ri";
import "./products.css";
import { IoMdCart } from "react-icons/io";
import { RxDividerHorizontal } from "react-icons/rx";
import getImageUrl from "../../utils/getImageUrl";

const getFirstAromaAndSize = (product) => {
  if (!product?.aromas?.length) return null;
  for (const aroma of product.aromas) {
    if (aroma?.sizes?.length) {
      return { aroma, size: aroma.sizes[0] };
    }
  }
  return null;
};

const ProductPage = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [aromas, setAromas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);
  const [aromasExpanded, setAromasExpanded] = useState(false);
  const [sizesExpanded, setSizesExpanded] = useState(false);
  const [uniqueValues, setUniqueValues] = useState({
    aromas: new Set(),
    categories: new Set(),
    sizes: new Set(),
  });
  const [sidebarActive, setSidebarActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState("default");
  const productsPerPage = 9;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get("/api/products");
        setProducts(response.data);
        setFilteredProducts(response.data);

        const values = {
          aromas: new Set(),
          categories: new Set(),
          sizes: new Set(),
        };

        response.data.forEach((product) => {
          if (product.categoryName) {
            values.categories.add(product.categoryName);
          }
          if (!product?.aromas?.length) return;
          product.aromas.forEach(aroma => {
            if (!aroma?.name) return;
            values.aromas.add(aroma.name);
          });
          product.aromas.forEach(aroma => {
            (aroma?.sizes || []).forEach(size => {
              values.sizes.add(`${size.size}${size.unit}`);
            });
          });
        });

        setUniqueValues(values);

        const passedCategory = location.state?.categoryName || null;
        if (passedCategory) {
          setCategories([passedCategory]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [location.state]);

  useEffect(() => {
    let result = products;

    // Filter by price range (keep products with no size/pricing data)
    result = result.filter((product) => {
      const pricedSizes = (product.aromas || []).flatMap(aroma =>
        (aroma?.sizes || []).filter(size => size?.sellingPrice != null && size.sellingPrice !== '')
      );
      if (pricedSizes.length === 0) return true;
      return pricedSizes.some(size =>
        size.sellingPrice >= priceRange[0] && size.sellingPrice <= priceRange[1]
      );
    });

    // Filter by aromas
    if (aromas.length > 0) {
      result = result.filter((product) =>
        (product.aromas || []).some(aroma => aromas.includes(aroma.name))
      );
    }

    // Filter by categories
    if (categories.length > 0) {
      result = result.filter((product) =>
        categories.includes(product.categoryName)
      );
    }

    // Filter by sizes
    if (sizes.length > 0) {
      result = result.filter((product) =>
        (product.aromas || []).some(aroma =>
          (aroma?.sizes || []).some(size => sizes.includes(`${size.size}${size.unit}`))
        )
      );
    }

    setFilteredProducts(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [priceRange, aromas, categories, sizes, products]);

  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const handleInputChange = (index, event) => {
    let value = event.target.value === "" ? "" : Number(event.target.value);
    let newPriceRange = [...priceRange];

    newPriceRange[index] = value;

    if (
      newPriceRange[0] >= 0 &&
      newPriceRange[1] <= 500 &&
      newPriceRange[0] <= newPriceRange[1]
    ) {
      setPriceRange(newPriceRange);
    }
  };

  const handleCheckboxChange = (filterType, value) => {
    if (filterType === "aromas") {
      setAromas((prev) => {
        return prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value];
      });
    } else if (filterType === "categories") {
      setCategories((prev) => {
        return prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value];
      });
    } else if (filterType === "sizes") {
      setSizes((prev) => {
        return prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value];
      });
    }
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const sizeA = getFirstAromaAndSize(a)?.size;
    const sizeB = getFirstAromaAndSize(b)?.size;
    if (!sizeA && !sizeB) return 0;
    if (!sizeA) return 1;
    if (!sizeB) return -1;

    const priceA = sizeA.sellingPrice;
    const priceB = sizeB.sellingPrice;
    const originalPriceA = sizeA.price;
    const originalPriceB = sizeB.price;
    const discountA = ((originalPriceA - priceA) / originalPriceA) * 100;
    const discountB = ((originalPriceB - priceB) / originalPriceB) * 100;
    const popularityA = a.popularity || 0;
    const popularityB = b.popularity || 0;

    switch (sortOption) {
      case "lowToHigh":
        return priceA - priceB;
      case "highToLow":
        return priceB - priceA;
      case "discount":
        return discountB - discountA; // Higher discount first
      case "popularity":
        return discountA - discountB; // Higher popularity first
      default:
        return 0; // Default (no sorting)
    }
  });


  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(sortedProducts.length / productsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePreview = (product) => {
    navigate("/preview", { state: { product } });
  };

  const toggleFilterVisibility = (filterType) => {
    if (filterType === "categories") {
      setCategoriesExpanded(!categoriesExpanded);
    } else if (filterType === "aromas") {
      setAromasExpanded(!aromasExpanded);
    } else if (filterType === "sizes") {
      setSizesExpanded(!sizesExpanded);
    }
  };

  return (
    <div>
      <div className="breadcrumb-wrap bg-f br-2">
        <div className="container" style={{marginBottom:'-90px'}}>
          <div className="breadcrumb-title">
            <h2>Products</h2>
            <ul className="breadcrumb-menu list-style">
              <li>
                <Link to="/">Home </Link>
              </li>
              <li>Products</li>
            </ul>
          </div>
        </div>
      </div>

      {/* <button
        className="filter-toggle-btn"
        onClick={() => setSidebarActive(!sidebarActive)}
      >
        <CiFilter /> Filter
      </button> */}

      <div className="containerorder" style={{ backgroundColor: "aliceblue" }}>
        <div className="row">
          <div className="col-xl-3 col-lg-5 order-xl-1 order-lg-2 order-md-2 order-2">
            <div className={`filters-section ${sidebarActive ? "active" : ""}`}>
              <button
                className="filter-toggle-btn1"
                onClick={() => setSidebarActive(!sidebarActive)}
              >
                ×
              </button>


              <div className="filter-group">
                <h4><RiFilter2Fill />Filters</h4>
                <br />

                <Box
                  sx={{
                    // width: 200,
                    margin: "auto",
                    textAlign: "center",
                    // overflow: "hidden",
                  }}
                >
                  <label className="filterheader"><p>Price</p> </label>

                  <Slider
                    value={priceRange}
                    onChange={handlePriceChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={500}
                    sx={{
                      width: "70%",
                      height: 5,
                      "& .MuiSlider-thumb": {
                        backgroundColor: " #0073ff",
                        borderRadius: "25px",
                        height: "12px",
                        width: "12px"
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: "#ddd",
                      },
                    }}
                  />
                </Box>

                <div className="priceinputcontiner">
                  <input
                    type="tel"
                    value={priceRange[0]}
                    style={{
                      width: "30%",
                      textAlign: "center",
                      borderRadius: "2px",
                    }}
                    className="inputpricemin new_karthik_heading_here"
                    onChange={(event) => handleInputChange(0, event)}
                  />
                  <RxDividerHorizontal />
                  <input
                    type="tel"
                    value={priceRange[1]}
                    style={{
                      width: "30%",
                      textAlign: "center",
                      borderRadius: "2px",
                    }}
                    className="inputpricemin new_karthik_heading_here"
                    onChange={(event) => handleInputChange(1, event)}
                  />
                </div>

              </div>


              <div className="filter-group">
                <label
                  onClick={() => toggleFilterVisibility("categories")}
                  className="filterheader"
                >
                  <p>Categories </p>
                  <p>{categoriesExpanded ? "▲" : "▼"}</p>
                </label>
                {categoriesExpanded && (
                  <div>
                    {[...uniqueValues.categories].map((category) => (
                      <div key={category} className="d-flex">
                        <input
                          type="checkbox"
                          checked={categories.includes(category)}
                          onChange={() =>
                            handleCheckboxChange("categories", category)
                          }
                          className="karthik-input"
                          style={{ marginRight: "5px" }}
                        />
                        <label className="karthik-label">{category}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-group">
                <label
                  onClick={() => toggleFilterVisibility("sizes")}
                  className="filterheader fliter_fontsize"
                >
                  <p>Sizes </p>
                  <p>{sizesExpanded ? "▲" : "▼"}</p>
                </label>
                {sizesExpanded && (
                  <div>
                    {[...uniqueValues.sizes]
                      .sort((a, b) => {
                        const parseSize = (size) => {
                          const match = size.match(/^(\d+)(ml|g|kg|l)$/);
                          if (!match) return Number.MAX_VALUE;
                          let value = parseInt(match[1], 10);
                          let unit = match[2];

                          if (unit === "kg") value *= 1000;
                          if (unit === "l") value *= 1000;

                          return value;
                        };

                        return parseSize(a) - parseSize(b);
                      })
                      .map((size) => (
                        <div key={size} className="d-flex">
                          <input
                            type="checkbox"
                            checked={sizes.includes(size)}
                            className="karthik-input"
                            onChange={() => handleCheckboxChange("sizes", size)}
                            style={{ marginRight: "5px" }}
                          />
                          <label className="karthik-label">{size}</label>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-xl-9 col-lg-12 order-xl-2 order-lg-1 order-md-1 order-1">
            <div className={`active-filters ${(categories.length > 0 || sizes.length > 0 || priceRange[0] !== 0 || priceRange[1] !== 500) ? '' : 'hidden'}`}>
              {categories.length > 0 &&
                categories.map((category, index) => (
                  <div key={`category-${index}`} className="filter-tag">
                    <span>{category}</span>
                    <button
                      onClick={() => {
                        setCategories(
                          categories.filter((item) => item !== category)
                        );
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}

              {sizes.length > 0 &&
                sizes.map((size, index) => (
                  <div key={`size-${index}`} className="filter-tag">
                    <span>{size}</span>
                    <button
                      onClick={() => {
                        setSizes(sizes.filter((item) => item !== size));
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}

              {(priceRange[0] !== 0 || priceRange[1] !== 500) && (
                <div className="filter-tag">
                  <span>
                    Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
                  </span>
                  <button onClick={() => setPriceRange([0, 500])}>X</button>
                </div>
              )}
            </div>
            <div className="sort-container">
              <div>
                <button
                  className="filter-toggle-btn"
                  onClick={() => setSidebarActive(!sidebarActive)}
                >
                  <CiFilter /> Filter
                </button>
              </div>
              <div>

                <select onChange={(e) => setSortOption(e.target.value)} className="select_option_new">
                  <option value="default">Default</option>
                  <option value="lowToHigh">Price: Low to High</option>
                  <option value="highToLow">Price: High to Low</option>
                  <option value="discount">Discount: High to Low</option>
                  <option value="popularity">Popularity: High to Low</option>
                </select>
              </div>
            </div>

            <div className="product-grid">
              {currentProducts.map((product) => {
                const first = getFirstAromaAndSize(product);
                const firstAroma = first?.aroma;
                const displaySize = first?.size;
                const isInStock = displaySize ? displaySize.quantity > 0 : false;

                return (
                  <div
                    className="product-card_karthik_new_card"
                    key={product._id}
                    onClick={() => handlePreview(product._id)}
                  >
                    {displaySize?.offer > 0 && (
                      <div className="badge-overlay">
                        <span className="top-left badge orange">{displaySize.offer}% Off</span>
                      </div>
                    )}

                    {firstAroma?.images?.length > 0 && (
                      <div className="product-tumb_karthik_new_card">
                        <div style={{ display: "flex", justifyContent: "space-evenly" }}>
                          <img
                            src={getImageUrl(firstAroma.images[0])}
                            alt={product.productName}
                            className="product_image1_karthik"
                            loading="lazy"
                          />
                         
                        </div>
                      </div>
                    )}

                    <div className="product-details_karthik_new_card">
                      <div className="new_redesign_first">
                        <div>
                          <h6 style={{ fontSize: "12px", textAlign: "Left" }}>{product.productName}</h6>
                        </div>
                        <div>
                          {displaySize ? (
                            <h6 style={{ color: "green", fontSize: "18px" }} className="new_karthik_heading_here">
                              ₹{displaySize.sellingPrice}.00
                            </h6>
                          ) : (
                            <h6 style={{ color: "#666", fontSize: "14px" }}>Price unavailable</h6>
                          )}
                        </div>
                      </div>
                      <div className="new_redesign_first">
                        <div>
                          <h6 style={{ fontSize: "12px" }}>{firstAroma?.name || product.categoryName}</h6>
                        </div>
                        {displaySize && (
                          <div>
                            <h6 style={{ color: "#000", fontSize: "8px", textAlign: "right" }}>(Incl. of all taxes)</h6>
                            <div className="redesign_linethrough_price">
                              <h6 style={{ color: "#000", fontSize: "14px", textAlign: "right" }} className="new_karthik_heading_here">
                                MRP.₹{displaySize.price}.00
                              </h6>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="product-bottom-details_karthik_new_card">
                        <button className="shop_now_new_karthik">Add To Cart &nbsp;<IoMdCart size={24} /></button>
                        <div className="product-links_karthik_new_card">
                          {displaySize && !isInStock && (
                            <div className="out-of-stock-label">Out of Stock</div>
                          )}
                          {!displaySize && (
                            <div className="out-of-stock-label">Details pending</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>


            <div className="ppagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
                style={{marginRight:"5px"}}
              >
               {"<"}
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
                style={{marginLeft:"5px"}}
              >
               {">"}

              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
























